import React, { useState, useEffect, useRef } from "react";
import { FaCheck, FaPen, FaTimes, FaTrash } from "react-icons/fa";
import EventForm from "./EventForm";
import EventDetail from "./EventDetail";
import { apiFetch } from "../utils/api";
import { formatDateWithWeekday } from "../utils/dateFormat";
import { useNotifications } from "./NotificationProvider";

const YEAR_ANIMATION_MS = 280;
const toDateInputValue = (value) => {
  const raw = String(value || "");
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  if (!value) return "";
  return new Date(value).toISOString().slice(0, 10);
};

const DatenSection = () => {
  const { showNotification } = useNotifications();
  const [events, setEvents] = useState([]);
  const [user, setUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [transitionStage, setTransitionStage] = useState("idle");
  const [nextYear, setNextYear] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEventId, setEditingEventId] = useState(null);
  const [deleteConfirmEventId, setDeleteConfirmEventId] = useState(null);
  const [eventDraft, setEventDraft] = useState({ name: "", start_date: "", end_date: "" });
  const touchStartX = useRef(null);

  const handleEventCreated = (newEvent) => {
    setEvents(prev => [...prev, newEvent]);
    const year = new Date(newEvent.start_date).getFullYear();
    setSelectedYear(year);
    setShowForm(false);
  };

  const handleEventUpdated = (updatedEvent) => {
    setEvents(prev => prev.map(event => event.id === updatedEvent.id ? { ...event, ...updatedEvent } : event));
    setSelectedEvent(prev => prev ? { ...prev, ...updatedEvent } : prev);
    setSelectedYear(new Date(updatedEvent.start_date).getFullYear());
  };

  const handleEventDeleted = (eventId) => {
    setEvents(prev => prev.filter(event => event.id !== eventId));
    setSelectedEvent(null);
  };

  const startEventEdit = (event, e) => {
    e.stopPropagation();
    setDeleteConfirmEventId(null);
    setEditingEventId(event.id);
    setEventDraft({
      name: event.name || "",
      start_date: toDateInputValue(event.start_date),
      end_date: toDateInputValue(event.end_date),
    });
  };

  const cancelEventEdit = (e) => {
    e.stopPropagation();
    setEditingEventId(null);
    setEventDraft({ name: "", start_date: "", end_date: "" });
  };

  const saveEventEdit = async (event, e) => {
    e.preventDefault?.();
    e.stopPropagation();
    if (!eventDraft.name.trim()) {
      showNotification("error", "Der Eventname ist erforderlich.");
      return;
    }
    if (!eventDraft.start_date || !eventDraft.end_date) {
      showNotification("error", "Start- und Enddatum sind erforderlich.");
      return;
    }
    if (new Date(eventDraft.start_date) > new Date(eventDraft.end_date)) {
      showNotification("error", "Das Enddatum darf nicht vor dem Startdatum liegen.");
      return;
    }

    try {
      const requestUpdate = (confirmed = false) => apiFetch(
        `/api/events/${event.id}${confirmed ? "?deleteData=1" : ""}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: eventDraft.name.trim(),
            start_date: eventDraft.start_date,
            end_date: eventDraft.end_date,
          }),
        }
      );
      let updated;
      try {
        updated = await requestUpdate();
      } catch (err) {
        if (!err.requiresConfirmation) throw err;
        const confirmed = window.confirm(
          `${err.message}\n\n${err.accountingRows || "Einige"} Abrechnung(en) liegen außerhalb des neuen Zeitraums und werden gelöscht. Zeitraum trotzdem ändern?`
        );
        if (!confirmed) return;
        updated = await requestUpdate(true);
      }
      handleEventUpdated(updated);
      setEditingEventId(null);
      setEventDraft({ name: "", start_date: "", end_date: "" });
      showNotification("success", "Event wurde aktualisiert.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const startDeleteConfirm = (event, e) => {
    e.stopPropagation();
    setEditingEventId(null);
    setDeleteConfirmEventId(event.id);
  };

  const cancelDeleteConfirm = (e) => {
    e.stopPropagation();
    setDeleteConfirmEventId(null);
  };

  const confirmDeleteEvent = async (event, e) => {
    e.stopPropagation();
    try {
      await apiFetch(`/api/events/${event.id}`, { method: "DELETE" });
      handleEventDeleted(event.id);
      setDeleteConfirmEventId(null);
      showNotification("success", "Event wurde gelöscht.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const openEvent = (event) => {
    if (editingEventId || deleteConfirmEventId) return;
    setSelectedEvent(event);
  };

  useEffect(() => {
    apiFetch("/api/auth/me")
      .then(userData => {
        setUser(userData);
        const endpoint = userData.accountType === "bedienung"
          ? "/api/events/assigned"
          : "/api/events";
        return apiFetch(endpoint);
      })
      .then(eventsData => {
        setEvents(eventsData);
      })
      .catch(err => {
        console.error(err);
        if (!err.sessionExpired) showNotification("error", err.message);
      });
  }, [showNotification]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (!touchStartX.current) return;
    const diffX = e.touches[0].clientX - touchStartX.current;
    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        changeYear(selectedYear - 1);
      } else {
        changeYear(selectedYear + 1);
      }
      touchStartX.current = null;
    }
  };

  const handleTouchEnd = () => {
    touchStartX.current = null;
  };

  const changeYear = (newYear) => {
    if (transitionStage !== "idle") return;
    if (newYear === selectedYear) return;
    const directionOut = newYear > selectedYear ? "exit-left" : "exit-right";
    const directionIn = newYear > selectedYear ? "enter-right" : "enter-left";
    setTransitionStage(directionOut);
    setNextYear({ value: newYear, directionIn });
  };

  useEffect(() => {
    if (transitionStage.startsWith("exit")) {
      const timeout = setTimeout(() => {
        setSelectedYear(nextYear.value);
        setTransitionStage(nextYear.directionIn);
      }, YEAR_ANIMATION_MS);
      return () => clearTimeout(timeout);
    }
    if (transitionStage.startsWith("enter")) {
      const timeout = setTimeout(() => setTransitionStage("idle"), YEAR_ANIMATION_MS);
      return () => clearTimeout(timeout);
    }
  }, [transitionStage, nextYear]);

  const filteredEvents = Array.isArray(events)
    ? events.filter(e => new Date(e.start_date).getFullYear() === selectedYear)
    : [];

  if (selectedEvent) {
    return (
      <EventDetail
        event={selectedEvent}
        onBack={() => setSelectedEvent(null)}
        onEventUpdated={handleEventUpdated}
        onEventDeleted={handleEventDeleted}
      />
    );
  }

  return (
    <div className="daten-section">
      <div className="year-carousel">
        <button className="year-nav" onClick={() => changeYear(selectedYear - 1)}>
          &#8592;
        </button>
        <div className="year-display-wrapper">
          <div className={`year-display ${transitionStage}`}>{selectedYear}</div>
        </div>
        <button className="year-nav" onClick={() => changeYear(selectedYear + 1)}>
          &#8594;
        </button>
      </div>

      <div className={`tile-grid ${transitionStage}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {filteredEvents.map(event => {
          const isEditing = editingEventId === event.id;
          const isDeleting = deleteConfirmEventId === event.id;
          return (
          <div className={`tile event-tile ${isEditing ? "editing" : ""} ${isDeleting ? "deleting" : ""}`} key={event.id} onClick={() => openEvent(event)}>
            {user?.accountType !== "bedienung" && !isDeleting && (
              <>
                {isEditing ? (
                  <button type="button" className="event-tile-action left" onClick={cancelEventEdit} aria-label="Bearbeiten abbrechen">
                    <FaTimes />
                  </button>
                ) : (
                  <button type="button" className="event-tile-action left danger" onClick={(e) => startDeleteConfirm(event, e)} aria-label="Event löschen">
                    <FaTrash />
                  </button>
                )}
                <button
                  type="button"
                  className="event-tile-action right"
                  onClick={isEditing ? (e) => saveEventEdit(event, e) : (e) => startEventEdit(event, e)}
                  aria-label={isEditing ? "Event speichern" : "Event bearbeiten"}
                >
                  {isEditing ? <FaCheck /> : <FaPen />}
                </button>
              </>
            )}
            {isDeleting && (
              <>
                <button type="button" className="event-tile-action left" onClick={cancelDeleteConfirm} aria-label="Löschen abbrechen">
                  <FaTimes />
                </button>
                <button type="button" className="event-tile-action right danger" onClick={(e) => confirmDeleteEvent(event, e)} aria-label="Event löschen bestätigen">
                  <FaCheck />
                </button>
              </>
            )}
            <div className="event-tile-content">
              {isDeleting ? (
                <>
                  <h3>Event löschen?</h3>
                  <p>Das Event und alle darin enthaltenen Daten werden gelöscht.</p>
                </>
              ) : isEditing ? (
                <form className="event-tile-form" onClick={e => e.stopPropagation()} onSubmit={e => saveEventEdit(event, e)}>
                  <input
                    type="text"
                    value={eventDraft.name}
                    onChange={e => setEventDraft(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Eventname"
                    required
                  />
                  <input
                    type="date"
                    value={eventDraft.start_date}
                    onChange={e => setEventDraft(prev => ({ ...prev, start_date: e.target.value }))}
                    required
                  />
                  <input
                    type="date"
                    value={eventDraft.end_date}
                    onChange={e => setEventDraft(prev => ({ ...prev, end_date: e.target.value }))}
                    required
                  />
                </form>
              ) : (
                <>
                  <h3>{event.name}</h3>
                  <p>
                    {formatDateWithWeekday(event.start_date)}
                    {event.end_date && (
                      <> - {formatDateWithWeekday(event.end_date)}</>
                    )}
                  </p>
                  {event.veranstalter && (
                    <p>Veranstalter: {event.veranstalter}</p>
                  )}
                </>
              )}
            </div>
          </div>
          );
        })}
        {user?.accountType !== "bedienung" && (
          !showForm ? (
            <div className="tile" onClick={() => setShowForm(true)}>
              <div>
                <h3>+ Neues Event</h3>
                <p>Event hinzufügen</p>
              </div>
            </div>
          ) : (
            <div className="tile new-event-form">
              <EventForm onCreated={handleEventCreated} year={selectedYear} />
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default DatenSection;
